// Plain CommonJS data module (no `import`/`export` keywords) so it can be
// `require()`-d directly by server code (plain Node, no build step) while
// still being importable from the frontend's Babel/Webpack build via a
// named import - see src/config/configCategories.js, which re-exports these
// for the frontend, and server/api-util/listingFieldsConfig.js, which
// requires this file directly for the partner API.
//
// This is the single source of truth for which categories have a
// sub-category, which sub-categories have a size list, and which
// categories ask for a colour - Console cannot express these dependencies
// itself (see the comment in configCategories.js), so this hand-written
// data is what both the website and the partner API must agree on.

const CATEGORY_KEY = 'category';
const SUB_CATEGORY_KEY = 'subCategory';
const SIZE_KEY = 'size';
// The Console field is still keyed 'colors' even though it is labelled "Colour"
// there. Keeping the key means colours saved on existing listings stay readable.
const COLOUR_KEY = 'colors';

// 'clothing-4' <- ('clothing', 4) and 'shoes-3-5' <- ('shoes', 3.5)
const sizeOption = (prefix, value) => ({
  option: `${prefix}-${`${value}`.replace('.', '-')}`,
  label: `${value}`,
});

// Inclusive range that also copes with half steps (0.5) without float drift.
const range = (from, to, step) => {
  const scale = 1 / step;
  const values = [];
  for (let i = Math.round(from * scale); i <= Math.round(to * scale); i++) {
    values.push(i / scale);
  }
  return values;
};

// Dress sizes: 4, 6, 8 … 24. Shared by Fashion -> Clothing and
// Mother & Baby -> Maternity wear, because both use the same list.
const clothingSizeOptions = range(4, 24, 2).map(v => sizeOption('clothing', v));

// UK adult shoe sizes: 3, 3.5, 4 … 13.
const shoeSizeOptions = range(3, 13, 0.5).map(v => sizeOption('shoes', v));

// UK infant shoe sizes: 2, 2.5 … 9.5.
const infantShoeSizeOptions = range(2, 9.5, 0.5).map(v => sizeOption('infant', v));

// UK kids' shoe sizes. This list is deliberately NOT sorted: real UK kids'
// sizing runs up to 13.5 and then restarts at 1. That is not a typo.
const kidsShoeSizeOptions = [
  10,
  10.5,
  11,
  11.5,
  12,
  12.5,
  13,
  13.5,
  1,
  1.5,
  2,
  2.5,
  3,
  3.5,
  4,
  4.5,
  5,
  5.5,
  6,
].map(v => sizeOption('kids', v));

// Baby and toddler clothing is sized by age range, not by a number.
const babyClothingSizeOptions = [
  { option: 'baby-0-1m', label: '0 to 1 month' },
  { option: 'baby-0-3m', label: '0 to 3 months' },
  { option: 'baby-3-6m', label: '3 to 6 months' },
  { option: 'baby-6-9m', label: '6 to 9 months' },
  { option: 'baby-9-12m', label: '9 to 12 months' },
  { option: 'baby-12-18m', label: '12 to 18 months' },
  { option: 'baby-18-24m', label: '18 to 24 months' },
  { option: 'baby-2-3y', label: '2 to 3 years' },
  { option: 'baby-3-4y', label: '3 to 4 years' },
];

// The one colour list, shared by every category that asks for a colour. A single
// item has a single colour, so 'Multicoloured' is there for the items that have no
// one clear colour, e.g. a patterned dress or a striped scarf. The keys are the
// ones Console already used, so a colour saved before this change still resolves
// to a label.
const colourOptions = [
  { option: 'black', label: 'Black' },
  { option: 'white', label: 'White' },
  { option: 'grey', label: 'Grey' },
  { option: 'beige', label: 'Beige / Cream' },
  { option: 'brown', label: 'Brown' },
  { option: 'red', label: 'Red' },
  { option: 'pink', label: 'Pink' },
  { option: 'orange', label: 'Orange' },
  { option: 'yellow', label: 'Yellow' },
  { option: 'green', label: 'Green' },
  { option: 'blue', label: 'Blue' },
  { option: 'navy', label: 'Navy' },
  { option: 'purple', label: 'Purple' },
  { option: 'gold', label: 'Gold' },
  { option: 'silver', label: 'Silver' },
  // Console spells this option value the American way; the key has to match it
  // exactly or the option is dropped. Only the label is shown to anyone.
  { option: 'multicolored', label: 'Multicoloured' },
];

// The main category list. The 'category' field itself is a Console field, so
// Console still owns the labels — but the keys and the order live here for two
// reasons (Jira P25028-115):
//   1. a category added here works everywhere straight away, even before someone
//      adds it in Console;
//   2. the list is always shown in alphabetical order, whatever order Console
//      happens to store it in.
const categoryOptions = [
  { option: 'art', label: 'Art' },
  { option: 'books', label: 'Books' },
  { option: 'electronics', label: 'Electronics' },
  { option: 'fashion', label: 'Fashion' },
  { option: 'furniture', label: 'Furniture' },
  { option: 'health-and-beauty', label: 'Health & Beauty' },
  { option: 'home-and-garden', label: 'Home & Garden' },
  { option: 'jewellery', label: 'Jewellery' },
  { option: 'mother-and-baby', label: 'Mother & Baby' },
  { option: 'toys', label: 'Toys' },
  { option: 'other', label: 'Other' },
];

// 'Other' means "none of the above", so it is pinned to the bottom of the list
// instead of being sorted in with the rest.
const CATEGORY_OPTION_LAST = 'other';

/**
 * Sub-category options, grouped by the main category they belong to.
 * A category that is missing from this map simply has no sub-categories,
 * so neither the sub-category nor the size input is shown for it.
 */
const subCategoryOptionsByCategory = {
  fashion: [
    { option: 'clothing', label: 'Clothing' },
    { option: 'shoes', label: 'Shoes' },
  ],
  'mother-and-baby': [
    { option: 'baby-toddler-clothing', label: 'Baby/toddler clothing' },
    { option: 'infants-shoes', label: "Infant's Shoes" },
    { option: 'kids-shoes', label: "Kids's Shoes" },
    { option: 'maternity-wear', label: 'Maternity wear' },
  ],
};

/**
 * Size options, grouped by the sub-category they belong to.
 * An empty list means "no size input for this sub-category".
 */
const sizeOptionsBySubCategory = {
  clothing: clothingSizeOptions,
  shoes: shoeSizeOptions,
  'baby-toddler-clothing': babyClothingSizeOptions,
  'infants-shoes': infantShoeSizeOptions,
  'kids-shoes': kidsShoeSizeOptions,
  'maternity-wear': clothingSizeOptions,
};

/**
 * Colour options, grouped by the main category they belong to.
 * A category that is missing from this map does not ask for a colour, so neither
 * the colour input nor the colour filter is shown for it.
 */
const colourOptionsByCategory = {
  fashion: colourOptions,
  'mother-and-baby': colourOptions,
};

// Keep the options that Console knows about, in the order this file defines,
// and fall back to this file's own options when Console has no matching ones.
// The fallback keeps the feature working before the Console fields are added,
// and keeps Console in charge of the labels once they are.
const mergeWithHostedOptions = (allowedOptions, hostedOptions) => {
  const hosted = allowedOptions.reduce((picked, allowed) => {
    const match = (hostedOptions || []).find(o => `${o.option}` === allowed.option);
    return match ? [...picked, { option: allowed.option, label: match.label }] : picked;
  }, []);
  return hosted.length > 0 ? hosted : allowedOptions;
};

module.exports = {
  CATEGORY_KEY,
  SUB_CATEGORY_KEY,
  SIZE_KEY,
  COLOUR_KEY,
  subCategoryOptionsByCategory,
  sizeOptionsBySubCategory,
  colourOptionsByCategory,
  categoryOptions,
  CATEGORY_OPTION_LAST,
  mergeWithHostedOptions,
};
